import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { deleteEvalDataset } from "../../utils/evaluation-functions";

// Eval datasets are tagged via the `{lambdaName}__{datasetName}` HF-repo
// prefix convention written by the admin-dashboard server when uploading
// to Hugging Face. The /api/evaluation/datasets endpoint already filters
// and parses these for us.
export default function DatasetsList({ datasets, onChanged }) {
  const navigate = useNavigate();
  const [openGroups, setOpenGroups] = useState({});
  const [deletingName, setDeletingName] = useState(null);

  const groups = useMemo(() => {
    if (!Array.isArray(datasets)) return null;
    const byLambda = new Map();
    for (const ds of datasets) {
      if (!ds?.lambda_name) continue;
      if (!byLambda.has(ds.lambda_name)) byLambda.set(ds.lambda_name, []);
      byLambda.get(ds.lambda_name).push(ds);
    }
    return [...byLambda.entries()]
      .map(([lambda, items]) => ({
        lambda,
        items: items.sort((a, b) => {
          const at = a.last_modified ? new Date(a.last_modified).getTime() : 0;
          const bt = b.last_modified ? new Date(b.last_modified).getTime() : 0;
          return bt - at;
        }),
      }))
      .sort((a, b) => a.lambda.localeCompare(b.lambda));
  }, [datasets]);

  const handleViewDetails = (datasetName) => {
    navigate(`/datasets/${datasetName}`, { state: { from: "/evaluation" } });
  };

  const handleDelete = async (datasetName) => {
    const confirmed = window.confirm(
      `Delete dataset "${datasetName}"? This permanently removes the Hugging Face repo and cannot be undone.`,
    );
    if (!confirmed) return;
    setDeletingName(datasetName);
    try {
      await deleteEvalDataset(datasetName);
      if (onChanged) await onChanged();
    } catch (error) {
      window.alert(`Failed to delete dataset: ${error.message || error}`);
    } finally {
      setDeletingName(null);
    }
  };

  const toggleGroup = (lambda) => {
    setOpenGroups((prev) => ({ ...prev, [lambda]: !(prev[lambda] ?? true) }));
  };

  if (groups === null) {
    return (
      <div className="flex justify-center py-8">
        <span className="w-6 h-6 border-2 border-dashed rounded-full animate-spin border-ktx-purple"></span>
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-100 text-center text-gray-500 space-y-2">
        <p>No evaluation datasets yet.</p>
        <p className="text-sm">
          Use the Create Dataset tab to generate one scoped to a specific lambda.
        </p>
        {onChanged && (
          <button
            type="button"
            onClick={onChanged}
            className="mt-2 px-3 py-1 text-sm text-ktx-purple underline"
          >
            Refresh
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {groups.map(({ lambda, items }) => {
        const isOpen = openGroups[lambda] ?? true;
        return (
          <div
            key={lambda}
            className="bg-white rounded-lg shadow-sm border border-gray-100 overflow-hidden"
          >
            <button
              type="button"
              onClick={() => toggleGroup(lambda)}
              className="w-full flex items-center justify-between px-5 py-3 bg-ktx-purple text-white"
            >
              <div className="flex items-center gap-3">
                <span className="font-semibold text-base">{lambda}</span>
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded">
                  {items.length} dataset{items.length === 1 ? "" : "s"}
                </span>
              </div>
              <ChevronDown
                className={`h-4 w-4 transition-transform ${
                  isOpen ? "rotate-180" : ""
                }`}
              />
            </button>

            {isOpen && (
              <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((dataset) => (
                  <div
                    key={dataset.repo_id || dataset.name}
                    className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow"
                  >
                    <div className="bg-gray-50 border-b border-gray-200 p-3">
                      <h3 className="font-medium truncate" title={dataset.name}>
                        {dataset.display_name || dataset.name}
                      </h3>
                      <p className="text-xs text-gray-500 truncate">
                        {dataset.repo_id || dataset.name}
                      </p>
                    </div>

                    <div className="p-4 space-y-3">
                      {dataset.last_modified && (
                        <p className="text-xs text-gray-500">
                          Updated{" "}
                          {new Date(dataset.last_modified).toLocaleString()}
                        </p>
                      )}
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleViewDetails(dataset.name)}
                          disabled={deletingName === dataset.name}
                          className="flex-1 bg-ktx-purple text-white py-2 px-3 rounded text-sm hover:bg-ktx-navy focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ktx-purple transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          View Details
                        </button>
                        <button
                          onClick={() => handleDelete(dataset.name)}
                          disabled={deletingName === dataset.name}
                          className="flex-1 bg-white text-ktx-red border border-current py-2 px-3 rounded text-sm hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {deletingName === dataset.name ? "Deleting..." : "Delete"}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
